import { Component, OnInit } from '@angular/core';
import { CitydealsFormGroup } from '@partneradmin/common';
import { FormControl, Validators } from '@angular/forms';
import { Store } from '@ngxs/store';
import { Router } from '@angular/router';
import { PartnerDetailService } from './details.service';
import { MessageService } from 'primeng/api';
import { TranslocoService } from '@ngneat/transloco';
import { PartnerDetailEntity, PartnerDetailRedeemablePlaceEntity } from './entity/partner-details.entity';

@Component({
  selector: 'partneradmin-details',
  templateUrl: './details.component.html',
  styleUrls: ['./details.component.scss'],
  providers: [PartnerDetailService, MessageService],
})
export class DetailsComponent implements OnInit {
  readonly companyNameControl = new FormControl('', Validators.required);
  readonly zipcodeControl = new FormControl('', Validators.required);
  readonly settlementControl = new FormControl('', Validators.required);
  readonly addressNameControl = new FormControl('', Validators.required);
  readonly addressTypeControl = new FormControl('', Validators.required);
  readonly addressNumControl = new FormControl('', Validators.required);
  readonly companyNumberControl = new FormControl('', Validators.required);
  readonly taxNumberControl = new FormControl('', Validators.required);
  readonly bankNameControl = new FormControl('');
  readonly invoiceNumberControl = new FormControl('');

  readonly form = new CitydealsFormGroup({
    company_name: this.companyNameControl,
    zipcode: this.zipcodeControl,
    settlement: this.settlementControl,
    address_name: this.addressNameControl,
    address_type: this.addressTypeControl,
    address_num: this.addressNumControl,
    company_number: this.companyNumberControl,
    tax_number: this.taxNumberControl,
    bank_name: this.bankNameControl,
    invoice_number: this.invoiceNumberControl,
  });

  selectedRedeemablePlace!: PartnerDetailRedeemablePlaceEntity | null;
  partnerDetailEntity!: PartnerDetailEntity | null;

  #store: Store;
  #router: Router;
  #messageService: MessageService;
  #translocoService: TranslocoService;
  #partnerDetailService: PartnerDetailService;

  constructor(
    store: Store,
    router: Router,
    messageService: MessageService,
    translocoService: TranslocoService,
    partnerDetailService: PartnerDetailService
  ) {
    this.#store = store;
    this.#router = router;
    this.#messageService = messageService;
    this.#translocoService = translocoService;
    this.#partnerDetailService = partnerDetailService;
  }

  ngOnInit(): void {
    this.getPartnerDetails();
  }

  /**
   * Fetch the partner detail entity from the backend and seed the form with
   * its values. The first redeemable place is preselected so the user can
   * immediately edit it without picking from the dropdown.
   */
  getPartnerDetails() {
    this.#partnerDetailService.getPartnerDetail().subscribe({
      next: (response: PartnerDetailEntity) => {
        this.partnerDetailEntity = response;
        this.partnerDetailEntity.redeemable_places = this.partnerDetailEntity.redeemable_places ?? [];
        this.partnerDetailEntity.redeemable_places.sort((item1, item2) =>
          item1.place_name.localeCompare(item2.place_name)
        );
        this.selectedRedeemablePlace = this.partnerDetailEntity.redeemable_places[0] ?? null;
        this.populateForm(response);
      },
      error: error => {
        this.#messageService.add({
          key: 'partner-details',
          severity: 'error',
          summary: this.#translocoService.translate('PAGE.VOUCHER_CHECK.FORM.ERROR'),
          detail: error.error.message,
        });
      },
    });
  }

  /**
   * Copy the backend entity values into the form controls. The
   * `billing_address_name` and `billing_address_num` fields are joined into
   * the single "Utca, házszám" form control with a space separator.
   *
   * @param entity Loaded partner detail entity.
   */
  private populateForm(entity: PartnerDetailEntity): void {
    this.form.patchValue({
      company_name: entity.company_name ?? '',
      zipcode: entity.zipcode ?? '',
      settlement: entity.settlement ?? '',
      address_name: entity.address_name ?? '',
      address_type: entity.address_type ?? '',
      address_num: entity.address_num ?? '',
      company_number: entity.company_number ?? '',
      tax_number: entity.tax_number ?? '',
      bank_name: entity.bank_name ?? '',
      invoice_number: entity.invoice_number ?? '',
    });
  }

  /**
   * Validate the form and, when valid, build the save payload. The actual
   * HTTP request is intentionally left as a TODO until the backend endpoint
   * is provided.
   */
  onSubmit(): void {
    this.form.submitted = true;
    this.form.markAllAsTouched();
    if (!this.form.valid) {
      return;
    }
    const payload = this.buildSavePayload();
    this.form.disable();
    this.#partnerDetailService.savePartnerDetail(payload).subscribe({
      next: response => {
        this.form.enable();
        this.partnerDetailEntity = { ...this.partnerDetailEntity, ...response };
        this.#messageService.add({
          key: 'partner-details',
          severity: 'success',
          summary: this.#translocoService.translate(
            'PAGE.PARTNER_DETAILS.FORM.SAVE_SUCCESS_NOTIFI'
          ),
        });
      },
      error: error => {
        this.form.enable();
        this.#messageService.add({
          key: 'partner-details',
          severity: 'error',
          summary: this.#translocoService.translate(
            'PAGE.PARTNER_DETAILS.FORM.REMOTE_ERROR_NOTIFI_TITLE'
          ),
          detail: error?.error?.message,
        });
      },
    });
  }

  /**
   * Build the save payload by merging the loaded entity with current form
   * values. The combined "Utca, házszám" field is split back into
   * `address_name` and `address_num`. The redeemable place
   * array is taken from the entity, which is mutated in-place via the
   * template's ngModel bindings.
   *
   * @returns A `Partial<PartnerDetailEntity>` ready to send to the backend.
   */
  private buildSavePayload(): Partial<PartnerDetailEntity> {
    return {
      ...this.partnerDetailEntity,
      company_name: this.companyNameControl.value ?? '',
      zipcode: this.zipcodeControl.value ?? '',
      settlement: this.settlementControl.value ?? '',
      address_name: this.addressNameControl.value ?? '',
      address_type: this.addressTypeControl.value ?? '',
      address_num: this.addressNumControl.value ?? '',
      company_number: this.companyNumberControl.value ?? '',
      tax_number: this.taxNumberControl.value ?? '',
      bank_name: this.bankNameControl.value ?? '',
      invoice_number: this.invoiceNumberControl.value ?? '',
      redeemable_places: this.partnerDetailEntity?.redeemable_places ?? [],
    };
  }

  /**
   * Concatenate the street name and house number into a single display
   * string. Nullish parts are treated as empty strings.
   *
   * @param name Street name, possibly nullish.
   * @param num  House number, possibly nullish.
   * @returns Space-separated combined string, trimmed.
   */
  private joinStreet(name: string | null | undefined, num: string | null | undefined): string {
    return `${name ?? ''} ${num ?? ''}`.trim();
  }

  /**
   * Split a combined "street + number" string into its parts. The last
   * whitespace-separated token is treated as the house number; everything
   * preceding it is the street name. If the input has no whitespace, the
   * whole value goes into the street name and the house number is empty.
   *
   * @param combined User-entered combined value.
   * @returns Object containing `name` (street) and `num` (house number).
   */
  private splitStreet(combined: string): { name: string; num: string } {
    const trimmed = combined.trim();
    const lastSpace = trimmed.lastIndexOf(' ');
    if (lastSpace < 0) {
      return { name: trimmed, num: '' };
    }
    return {
      name: trimmed.slice(0, lastSpace).trim(),
      num: trimmed.slice(lastSpace + 1).trim(),
    };
  }
}
